import React, { useState, useEffect, useRef } from "react";
import { PieChart, Pie, Cell } from "recharts";
import { Link } from "react-router-dom";

const Overview = () => {
  const minAmount = 50000;
  const maxAmount = 500000;
  const [activeIndex, setActiveIndex] = useState(null);
  const [customerTab, setCustomerTab] = useState("new");
  const [infoTab, setInfoTab] = useState("overview");
  const [showTracker, setShowTracker] = useState(false);
  const [loanType, setLoanType] = useState("home");
  const [amountPercentage, setAmountPercentage] = useState(0);
  const [loanAmount, setLoanAmount] = useState(minAmount);
  const [loanDuration, setLoanDuration] = useState(12);
  const [durationType, setDurationType] = useState("months");
  const [interestRate, setInterestRate] = useState(10);
  const [emi, setEmi] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);

  const calculatePercentage = (amount) =>
    Math.round(((amount - minAmount) / (maxAmount - minAmount)) * 100);

  const calculateAmount = (percentage) =>
    Math.round(minAmount + (percentage / 100) * (maxAmount - minAmount));

  const handlePercentageChange = (e) => {
    const newPercentage = Number(e.target.value);
    setAmountPercentage(newPercentage);
    setLoanAmount(calculateAmount(newPercentage));
  };

  useEffect(() => {
    setAmountPercentage(calculatePercentage(loanAmount));
  }, [loanAmount]);

  const calculateEMI = () => {
    const monthlyInterestRate = interestRate / 1200;
    const totalMonths =
      durationType === "years" ? loanDuration * 12 : loanDuration;
    const emiValue =
      (loanAmount *
        monthlyInterestRate *
        Math.pow(1 + monthlyInterestRate, totalMonths)) /
      (Math.pow(1 + monthlyInterestRate, totalMonths) - 1);

    const totalPayment = emiValue * totalMonths;
    const totalInterestPayment = totalPayment - loanAmount;

    setEmi(emiValue || 0);
    setTotalAmount(totalPayment || 0);
    setTotalInterest(totalInterestPayment || 0);
  };

  useEffect(() => {
    calculateEMI();
  }, [loanAmount, loanDuration, interestRate, durationType]);

  const pieData = [
    { name: "Principal Amount", value: totalAmount - totalInterest },
    { name: "Total Interest", value: totalInterest },
  ];

  const COLORS = ["#FF8C8B", "#8B96FF"];
  const amountSliderRef = useRef(null);
  const interestSliderRef = useRef(null);

  return (
    <>
      <section
        className=""
        style={{
          background: "linear-gradient(to top, transparent 0%, #F5F2FF 100%)",
        }}
      >
        <div className="container">
          <div className="row justify-content-center mb-4">
            <div className="col-md-12 mt-5 d-none d-lg-flex flex-wrap gap-3 text-start">
              {["overview", "documentation", "process", "emi"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setInfoTab(tab)}
                  className="btn fw-semibold px-4 py-2"
                  style={{
                    border: "none",
                    backgroundColor: infoTab === tab ? "#0d6efd" : "#fff",
                    color: infoTab === tab ? "#fff" : "#000",
                    borderRadius: "50px",
                    transition: "all 0.3s ease",
                    fontFamily: "poppins",
                  }}
                >
                  {
                    {
                      overview: "Overview",
                      documentation: "Documentation Needed",
                      process: "Application Process",
                      emi: "EMI Calculator",
                    }[tab]
                  }
                </button>
              ))}
            </div>

            <div
              className="d-flex d-lg-none overflow-auto mt-3 scrollbar-hidden gap-3 px-2"
              style={{ whiteSpace: "nowrap" }}
            >
              {["overview", "documentation", "process", "emi"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setInfoTab(tab)}
                  className="btn fw-semibold px-4 py-2 flex-shrink-0"
                  style={{
                    border: "none",
                    backgroundColor: infoTab === tab ? "#0d6efd" : "#fff",
                    color: infoTab === tab ? "#fff" : "#000",
                    borderRadius: "50px",
                    transition: "all 0.3s ease",
                    fontFamily: "poppins",
                  }}
                >
                  {
                    {
                      overview: "Overview",
                      documentation: "Documentation Needed",
                      process: "Application Process",
                      emi: "EMI Calculator",
                    }[tab]
                  }
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-12">
            {infoTab === "overview" && (
              <div className="container">
                <div
                  className="custom_card p-4"
                  style={{
                    border: "none",
                    boxShadow: "10px 10px 10px 0px #0000000A",
                    borderRadius: "1%",
                    background: "white",
                  }}
                >
                  <h4 className="fw-bold mb-3">Overview</h4>
                  <p className="text-muted">
                    A home loan is more than just a financial transaction—it's
                    the key to your dream home. Whether you're buying your first
                    house, upgrading, or investing in property, Shubh Paisa's
                    home loans offer the perfect blend of affordability and
                    convenience.
                  </p>
                </div>
              </div>
            )}

            {infoTab === "documentation" && (
              <div className="container">
                <div
                  className="custom_card p-4"
                  style={{
                    border: "none",
                    boxShadow: "0px 10px 10px 0px #0000000A",
                    background: "white",
                  }}
                >
                  <h4 className="fw-bold mb-3">Documentation Needed</h4>
                  <ul className="text-muted">
                    <li>Proof of Identity (Aadhar, PAN, Passport)</li>
                    <li>Address Proof</li>
                    <li>Income Documents (Salary slips, ITR)</li>
                    <li>Bank Statements</li>
                    <li>Property Documents</li>
                  </ul>
                </div>
              </div>
            )}

            {infoTab === "process" && (
              <div className="container">
                <div
                  className="custom_card p-4"
                  style={{
                    border: "none",
                    boxShadow: "0px 10px 10px 0px #0000000A",
                    background: "white",
                  }}
                >
                  <h4 className="fw-bold mb-3">Application Process</h4>
                  <ol className="text-muted">
                    <li>
                      Fill in the application form with your basic details
                    </li>
                    <li>Upload required documents securely</li>
                    <li>Choose loan amount and tenure</li>
                    <li>Submit the application for approval</li>
                    <li>Get loan approval and disbursement</li>
                  </ol>
                </div>
              </div>
            )}

            {infoTab === "emi" && (
              <div
                className="Emi-section d-flex align-items-center justify-content-center py-5"
                style={{ backgroundColor: "#FFEFCA", overflowX: "hidden" }} // 💡 Prevent horizontal scroll
              >
                <div className="container px-3 px-md-4 px-lg-5">
                  {" "}
                  {/* 💡 Responsive padding */}
                  <div className="row justify-content-center">
                    <div className="col-12 text-center mb-4">
                      <h4 className="fw-bold">
                        Calculate with Confidence, <br />
                        Plan with Ease!
                      </h4>
                    </div>

                    <div className="col-12">
                      <div className="row g-4">
                        {/* LEFT: Calculator Form */}
                        <div className="col-12 col-md-6">
                          <div className="card-body p-4">
                            <div className="mb-4">
                              <p
                                className="form-label medium fw-medium text-secondary"
                                style={{ fontFamily: "poppins" }}
                              >
                                Loan Type
                              </p>
                              <select
                                className="form-select rounded-3"
                                value={loanType}
                                onChange={(e) => setLoanType(e.target.value)}
                              >
                                <option value="home">Home Loan</option>
                                <option value="personal">Personal Loan</option>
                                <option value="car">Car Loan</option>
                                <option value="education">
                                  Education Loan
                                </option>
                              </select>
                            </div>

                            <div className="mb-4">
                              <p className="form-label small fw-medium text-secondary">
                                Loan Amount (₹)
                              </p>
                              <input
                                type="number"
                                className="form-control rounded-3 text-start"
                                style={{ fontFamily: "poppins" }}
                                value={loanAmount}
                                onChange={(e) =>
                                  setLoanAmount(Number(e.target.value))
                                }
                              />
                              <div className="mt-2">
                                <input
                                  type="range"
                                  className="custom-range w-100 border-0"
                                  style={{ fontFamily: "poppins" }}
                                  min="0"
                                  max="100"
                                  step="1"
                                  value={amountPercentage}
                                  onChange={handlePercentageChange}
                                  ref={amountSliderRef}
                                />
                                <div
                                  className="d-flex justify-content-between text-start small text-primary mb-1"
                                  style={{ fontFamily: "poppins" }}
                                >
                                  <span>₹{minAmount.toLocaleString()}</span>
                                  <span>₹{maxAmount.toLocaleString()}</span>
                                </div>
                              </div>
                            </div>

                            <div className="mb-4">
                              <p
                                className="form-label small fw-medium text-secondary"
                                style={{ fontFamily: "poppins" }}
                              >
                                Loan Duration
                              </p>
                              <div className="input-group align-items-center">
                                <input
                                  type="number"
                                  className="form-control rounded-3 text-start"
                                  value={loanDuration}
                                  onChange={(e) =>
                                    setLoanDuration(Number(e.target.value))
                                  }
                                />
                                <div
                                  className="ms-2 position-relative rounded d-flex p-1"
                                  style={{
                                    width: "130px",
                                    height: "34px",
                                    border: "0px solid #0d6efd", // active border color
                                    borderRadius: "999px",
                                    backgroundColor: "#ffffff",
                                    overflow: "hidden",
                                  }}
                                >
                                  {/* Sliding active background */}
                                  <div
                                    className="position-absolute bg-primary rounded-pill"
                                    style={{
                                      width: "50%",
                                      height: "70%", // Adjust height as needed
                                      top: "15%", // (100% - 70%) / 2 to center vertically
                                      left:
                                        durationType === "months"
                                          ? "0%"
                                          : "50%",
                                      transition: "left 0.3s ease",
                                      zIndex: 0,
                                    }}
                                  ></div>

                                  {/* Mo button */}
                                  <button
                                    type="button"
                                    className={`btn btn-sm fw-medium w-50 border-0 text-center ${
                                      durationType === "months"
                                        ? "text-white"
                                        : "text-primary"
                                    }`}
                                    onClick={() => setDurationType("months")}
                                    style={{
                                      background: "transparent",
                                      fontFamily: "poppins",
                                      zIndex: 1,
                                      borderRadius: "999px",
                                    }}
                                  >
                                    Mo
                                  </button>

                                  {/* Yr button */}
                                  <button
                                    type="button"
                                    className={`btn btn-sm fw-medium w-50 border-0 text-center ${
                                      durationType === "years"
                                        ? "text-white"
                                        : "text-primary"
                                    }`}
                                    onClick={() => setDurationType("years")}
                                    style={{
                                      background: "transparent",
                                      fontFamily: "poppins",
                                      zIndex: 1,
                                      borderRadius: "999px",
                                    }}
                                  >
                                    Yr
                                  </button>
                                </div>
                              </div>
                            </div>

                            <div className="mb-4">
                              <label
                                className="form-label small fw-medium text-secondary"
                                style={{ fontFamily: "poppins" }}
                              >
                                Rate of Interest
                              </label>
                              <input
                                type="number"
                                className="form-control rounded-3 text-start"
                                value={interestRate}
                                step="0.1"
                                onChange={(e) =>
                                  setInterestRate(Number(e.target.value))
                                }
                              />
                            </div>
                            <div className="mt-2">
                              <div
                                className="d-flex justify-content-between small text-primary mb-1"
                                style={{ fontFamily: "poppins" }}
                              >
                                <span>10% p.a.</span>
                                <span>30% p.a.</span>
                              </div>
                              <input
                                type="range"
                                className="custom-range w-100"
                                min="10"
                                max="30"
                                step="0.1"
                                value={interestRate}
                                onChange={(e) =>
                                  setInterestRate(Number(e.target.value))
                                }
                                ref={interestSliderRef}
                              />
                            </div>
                            <div className="d-flex justify-content-start justify-content-md-start mt-4 w-100 w-md-auto">
                              <Link
                                to="/EMIPAGE"
                                className="btn text-white fw-semibold d-flex align-items-center px-3 px-md-4 py-1 py-md-2 rounded-4 border-0 bg-primary"
                                style={{
                                  fontFamily: "poppins",
                                  textDecoration: "none",
                                }}
                              >
                                Apply Now
                                <i
                                  className="fas fa-arrow-right ms-2 ms-md-3"
                                  style={{ color: "white", fontSize: "0.9rem" }}
                                ></i>
                              </Link>
                            </div>
                          </div>
                        </div>
                        {/* RIGHT: Chart and EMI Info */}
                        <div className="col-12 col-md-6">
                          <div className="card bg-white border-0 h-100 rounded-4 shadow-sm">
                            <div className="card-body">
                              {/* Pie Chart */}
                              <div className="row mb-4">
                                <div className="col-12 d-flex justify-content-center">
                                  <PieChart width={200} height={200}>
                                    <Pie
                                      data={pieData}
                                      cx="50%"
                                      cy="50%"
                                      innerRadius={60}
                                      outerRadius={80}
                                      dataKey="value"
                                    >
                                      {pieData.map((entry, index) => (
                                        <Cell
                                          key={`cell-${index}`}
                                          fill={COLORS[index % COLORS.length]}
                                          stroke="#fff"
                                          strokeWidth={2}
                                        />
                                      ))}
                                    </Pie>
                                  </PieChart>
                                </div>
                              </div>

                              {/* EMI Details Cards */}
                              <div
                                className="emi-card mx-auto"
                                style={{
                                  backgroundColor: "#FAF9FF",
                                  borderRadius: "1rem",
                                  padding: "1rem",
                                  maxWidth: "380px",
                                  width: "100%",
                                }}
                              >
                                <div className="row g-3">
                                  <div className="col-6">
                                    <div className="card border-0 h-100 bg-light rounded-3">
                                      <div className="card-body text-center d-flex flex-column justify-content-center h-100">
                                        <p className="small text-dark mb-1">
                                          Total Amount
                                        </p>
                                        <div className="d-flex justify-content-center align-items-center gap-2">
                                          <div
                                            className="rounded-circle"
                                            style={{
                                              width: "14px",
                                              height: "14px",
                                              backgroundColor: "#dc3545",
                                            }}
                                          ></div>
                                          <p
                                            className="h5 text-dark fw-bold mb-0"
                                            style={{ fontFamily: "poppins" }}
                                          >
                                            ₹{totalAmount.toFixed(2)}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-6">
                                    <div className="card border-0 h-100 bg-light rounded-3">
                                      <div className="card-body text-center d-flex flex-column justify-content-center h-100">
                                        <p className="small text-dark mb-1">
                                          Total Interest
                                        </p>
                                        <div className="d-flex justify-content-center align-items-center gap-2">
                                          <div
                                            className="rounded-circle"
                                            style={{
                                              width: "14px",
                                              height: "14px",
                                              backgroundColor: "blue",
                                            }}
                                          ></div>
                                          <p
                                            className="h5 text-dark fw-bold mb-0"
                                            style={{ fontFamily: "poppins" }}
                                          >
                                            ₹{totalInterest.toFixed(2)}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Monthly EMI Box */}
                              <div
                                className="mt-4 mx-auto"
                                style={{
                                  maxWidth: "400px",
                                  width: "100%",
                                }}
                              >
                                <div className="card bg-primary text-white rounded-4">
                                  <div className="card-body text-center">
                                    <p className="small mb-1 text-white">
                                      Monthly EMI
                                    </p>
                                    <p
                                      className="h4 text-white fw-bold mb-0"
                                      style={{ fontFamily: "poppins" }}
                                    >
                                      ₹{emi.toFixed(2)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>{" "}
                            {/* card-body */}
                          </div>{" "}
                          {/* right card */}
                        </div>{" "}
                        {/* col-6 */}
                      </div>{" "}
                      {/* row */}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Overview;
